import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import classNames from 'classnames';
import { uniqueId } from 'lodash';

import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

import EditorInput from './EditorInput';
import { Icon } from 'js/components';
import { faChevronDown, faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
  faGripVertical,
  faExclamationTriangle,
  faWeightHanging,
} from '@fortawesome/pro-solid-svg-icons';
import { UncontrolledTooltip } from 'reactstrap';
import ErrorMessage from 'js/components/Assessment/Editors/common/components/EditorErrors/ErrorMessage';
import DropIndicator from 'js/components/Assessment/Editors/common/components/DropIndicator';
import RubricQuestionSource from './RubricQuestionSource';
import RubricQuestionPickerModal from './RubricQuestionPickerModal';
import { useRubricContext } from './RubricContext';
import { isNullOrWhitespace } from 'js/utils/string';
import RubricMeasurementActions from './RubricMeasurementActions';
import styles from './RubricEditor.module.scss';

const questionTextPlaceholder =
  'Question text: e.g. describe what you are scoring.';

function QuestionDragPreview({ question }) {
  return (
    <div className={classNames(styles.question, 'drag-preview')}>
      <div className={styles['question-header']}>
        <div className={styles['drag-handle']}>
          <span className="drag-handle">
            <Icon icon={faGripVertical} className="icon" />
          </span>
        </div>
        <div className={styles.key}>{question.key}</div>
      </div>
      <div className={classNames(styles['question-body'], 'line-clamp-2')}>
        {!isNullOrWhitespace(question.text)
          ? question.text
          : questionTextPlaceholder}
      </div>
    </div>
  );
}

function RubricQuestion({
  questionSetGuid,
  questionIndex,
  question,
  questionSetRetired = false,
  onRequestRemoveQuestion,
}) {
  const guidRef = useRef(uniqueId('rq-'));
  const rootRef = useRef(null);
  const dragHandleRef = useRef(null);
  const [collapsedSources, setCollapsedSources] = useState(true);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  const {
    updateQuestionField,
    errors,
    moveRubricQuestionBefore,
    moveRubricQuestionAfter,
    addRubricSourcesToQuestion,
    setRubricQuestionRetiredAt,
  } = useRubricContext();

  const questionGuid = question.guid;

  const handleFieldChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateQuestionField(questionSetGuid, questionGuid, name, value);
    },
    [questionSetGuid, questionGuid, updateQuestionField]
  );

  const toggleSources = useCallback(() => {
    setCollapsedSources(!collapsedSources);
  }, [collapsedSources]);

  const handleAddSourcesFromPicker = useCallback(
    (selectedQuestions) => {
      addRubricSourcesToQuestion(
        questionSetGuid,
        questionGuid,
        selectedQuestions
      );
      setCollapsedSources(false);
    },
    [addRubricSourcesToQuestion, questionSetGuid, questionGuid]
  );

  const handleFieldBlur = useCallback(
    (e) => {
      const { name } = e.target;
      const current = e.target.value;
      if (name === 'weight') {
        const parsed = parseFloat(String(current).replace(/[^0-9.-]/g, ''));
        const next = Number.isFinite(parsed) ? parsed : 1;
        updateQuestionField(questionSetGuid, questionGuid, name, next);
        return;
      }
      const next = String(current ?? '').trim();
      if (next !== current) {
        e.target.value = next;
      }
      updateQuestionField(questionSetGuid, questionGuid, name, next);
    },
    [questionSetGuid, questionGuid, updateQuestionField]
  );

  const fieldErrors = errors.questions[questionGuid] ?? {};
  const keyError = fieldErrors.key;

  useEffect(() => {
    const el = rootRef.current;
    const dragHandle = dragHandleRef.current;
    if (!el || !dragHandle) {
      return undefined;
    }

    return combine(
      draggable({
        element: el,
        dragHandle,
        getInitialData: () => ({
          rubricQuestion: question,
          questionSetGuid,
          index: questionIndex,
        }),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            render({ container }) {
              ReactDOM.render(
                <QuestionDragPreview question={question} />,
                container
              );
              return function cleanup() {
                ReactDOM.unmountComponentAtNode(container);
              };
            },
            getOffset: () => ({ x: 20, y: 16 }),
            nativeSetDragImage,
          });
        },
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) =>
          !!(
            source.data.rubricQuestion &&
            source.data.rubricQuestion.guid !== question.guid &&
            !source.data.rubricSource
          ),
        getData: (args) =>
          attachClosestEdge(
            { rubricQuestion: question, questionSetGuid, questionIndex },
            {
              input: args.input,
              element: args.element,
              allowedEdges: ['top', 'bottom'],
            }
          ),
        onDragEnter: () => setIsDraggedOver(true),
        onDragLeave: () => {
          setClosestEdge(null);
          setIsDraggedOver(false);
        },
        onDrag({ self, source }) {
          const isSource = source.element === el;
          if (
            isSource ||
            !source.data.rubricQuestion ||
            source.data.rubricSource
          ) {
            setClosestEdge(null);
            return;
          }

          const sameSet = source.data.questionSetGuid === questionSetGuid;
          if (sameSet) {
            const sourceIdx = source.data.index;
            const isItemBeforeSource = questionIndex === sourceIdx - 1;
            const isItemAfterSource = questionIndex === sourceIdx + 1;
            const edge = extractClosestEdge(self.data);
            const isDropIndicatorHidden =
              (isItemBeforeSource && edge === 'bottom') ||
              (isItemAfterSource && edge === 'top');
            if (isDropIndicatorHidden) {
              setClosestEdge(null);
              return;
            }
          }

          setClosestEdge(extractClosestEdge(self.data));
        },
        onDrop: ({ source, location }) => {
          setIsDraggedOver(false);
          const dropTarget = location.current.dropTargets[0];
          const edge = extractClosestEdge(dropTarget.data);
          setClosestEdge(null);

          const rq = source.data.rubricQuestion;
          const fromSet = source.data.questionSetGuid;
          if (!rq || !fromSet || !edge) {
            return;
          }

          if (edge === 'bottom') {
            moveRubricQuestionAfter(
              rq.guid,
              fromSet,
              questionSetGuid,
              question.guid
            );
          } else {
            moveRubricQuestionBefore(
              rq.guid,
              fromSet,
              questionSetGuid,
              question.guid
            );
          }
        },
        getIsSticky: (args) => {
          const elementUnderCursor = document.elementFromPoint(
            args.input.pageX,
            args.input.pageY
          );
          return (
            elementUnderCursor &&
            (elementUnderCursor.closest('.question-set-droppable-area') ||
              elementUnderCursor.closest('.rubric-source-droppable-area'))
          );
        },
      })
    );
  }, [
    question,
    question.guid,
    questionSetGuid,
    questionIndex,
    moveRubricQuestionBefore,
    moveRubricQuestionAfter,
  ]);

  const cardClassName = useMemo(
    () =>
      classNames(styles.question, {
        [styles.dragging]: dragging,
      }),
    [dragging]
  );

  const sources = question.sources ?? [];
  const canAddSources = !question.retired_at && !questionSetRetired;

  return (
    <div className={styles['question-slot']} ref={rootRef}>
      <div className={cardClassName}>
        <div className={styles['question-header']}>
          <button
            type="button"
            className="drag-handle"
            ref={dragHandleRef}
            aria-label="Drag to reorder or move question"
          >
            <Icon icon={faGripVertical} className="icon" />
          </button>
          <div className={styles['question-weight']}>
            <EditorInput
              name="weight"
              value={question.weight}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              convertToNumber
              defaultNumber={1}
              placeholder="question-weight"
            >
              <span title="Weight">
                <Icon
                  icon={faWeightHanging}
                  className="icon icon-left text-info"
                />
                {question.weight}
              </span>
            </EditorInput>
          </div>
          <div className={styles.key}>
            <EditorInput
              name="key"
              value={question.key}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              placeholder="question-key"
            />
          </div>
          {keyError && (
            <div className={styles.keyError} id={`${guidRef.current}-key`}>
              <Icon
                icon={faExclamationTriangle}
                className="icon icon-left text-danger"
              />
              <UncontrolledTooltip
                placement="top"
                target={`#${guidRef.current}-key`}
                className={styles.tooltip}
                trigger="click hover"
                autohide={false}
              >
                <ErrorMessage>{keyError}</ErrorMessage>
              </UncontrolledTooltip>
            </div>
          )}
          <div className={styles['question-actions']}>
            <RubricMeasurementActions
              ariaLabel="Remove question"
              modalTitle="Remove question?"
              detailPrimary={question.key}
              detailSecondary={question.text}
              retireDescription="Stop counting this question toward scores after the retirement date."
              retiredAt={question.retired_at}
              onRetire={(date) =>
                setRubricQuestionRetiredAt(questionSetGuid, questionGuid, date)
              }
              onClear={() =>
                setRubricQuestionRetiredAt(questionSetGuid, questionGuid, null)
              }
              onDelete={() =>
                onRequestRemoveQuestion?.(questionSetGuid, questionGuid)
              }
            />
          </div>
        </div>
        <div className={styles['question-body']}>
          <div className={styles['question-text']}>
            <EditorInput
              name="text"
              multiLine
              value={question.text}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={
                fieldErrors.text ? (
                  <ErrorMessage>{fieldErrors.text}</ErrorMessage>
                ) : null
              }
              placeholder="question-text"
            />
          </div>
        </div>
        <div className={styles['question-sources']}>
          <button
            type="button"
            className={cx(styles.expander, {
              [styles.collapsed]: collapsedSources,
            })}
            onClick={toggleSources}
          >
            <Icon icon={faChevronDown} className="icon icon-left" />
            {`${sources.length} ${sources.length === 1 ? 'source' : 'sources'}`}
          </button>
          {canAddSources && (
            <button
              type="button"
              className={cx(
                'btn btn-action-alt btn-primary',
                styles['add-source-button']
              )}
              onClick={() => setShowSourcePicker(true)}
            >
              <Icon icon={faPlus} className="icon icon-left" />
              Add source
            </button>
          )}
          <ol
            className={classNames(
              styles['source-list'],
              'rubric-source-droppable-area'
            )}
          >
            {sources.map((source, sourceIndex) => (
              <RubricQuestionSource
                key={source.guid}
                source={source}
                questionSetGuid={questionSetGuid}
                questionGuid={questionGuid}
                sourceIndex={sourceIndex}
                sourceCount={sources.length}
              />
            ))}
          </ol>
        </div>
      </div>
      {isDraggedOver && closestEdge !== null && (
        <DropIndicator
          top={closestEdge === 'top'}
          bottom={closestEdge === 'bottom'}
        />
      )}
      <RubricQuestionPickerModal
        isOpen={showSourcePicker}
        onClose={() => setShowSourcePicker(false)}
        onAddQuestions={handleAddSourcesFromPicker}
        pickerMode="addSource"
      />
    </div>
  );
}

export default RubricQuestion;
